import { Controller } from "@hotwired/stimulus"

/**
 * Optimistically reorders a list of items via up/down buttons, then
 * persists the new position by fetching a per-item URL stored in
 * `data-move-up-url` / `data-move-down-url` on each item element.
 *
 * Usage:
 *   <div data-controller="reorderable-list">
 *     <div data-reorderable-list-target="item"
 *          data-move-up-url="..." data-move-down-url="...">
 *       <span data-reorderable-list-target="indexLabel">1.</span> Program A
 *       <button data-action="click->reorderable-list#moveUp"
 *               data-reorderable-list-target="upButton">↑</button>
 *       <button data-action="click->reorderable-list#moveDown"
 *               data-reorderable-list-target="downButton">↓</button>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["item", "upButton", "downButton", "indexLabel"]

  declare readonly itemTargets: HTMLElement[]
  declare readonly upButtonTargets: HTMLButtonElement[]
  declare readonly downButtonTargets: HTMLButtonElement[]
  declare readonly indexLabelTargets: HTMLElement[]

  connect() {
    this.refresh()
  }

  // A removed card (turbo_stream.remove) renumbers the rest.
  itemTargetDisconnected() {
    this.refresh()
  }

  moveUp(event: Event) {
    const item = this.itemFromEvent(event)
    if (!item) return
    const prev = item.previousElementSibling as HTMLElement | null
    if (!prev) return
    item.parentElement!.insertBefore(item, prev)
    this.refresh()
    this.persist(item.dataset.moveUpUrl)
  }

  moveDown(event: Event) {
    const item = this.itemFromEvent(event)
    if (!item) return
    const next = item.nextElementSibling as HTMLElement | null
    if (!next) return
    item.parentElement!.insertBefore(next, item)
    this.refresh()
    this.persist(item.dataset.moveDownUrl)
  }

  private itemFromEvent(event: Event): HTMLElement | null {
    const origin =
      (event.currentTarget as HTMLElement | null) ||
      (event.target as HTMLElement | null)
    if (!origin || typeof origin.closest !== "function") return null

    return origin.closest<HTMLElement>('[data-reorderable-list-target~="item"]')
  }

  private refresh() {
    const items = this.itemTargets

    this.indexLabelTargets.forEach((label, i) => {
      label.textContent = `${i + 1}.`
    })

    this.upButtonTargets.forEach((btn, i) => {
      btn.disabled = i === 0
    })
    this.downButtonTargets.forEach((btn, i) => {
      btn.disabled = i === items.length - 1
    })
  }

  private async persist(url?: string) {
    if (!url) return

    const csrfToken =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || ""

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        credentials: "same-origin",
      })

      if (!response.ok) {
        console.error(
          `Failed to persist reorder: ${response.status} ${response.statusText}`,
        )
      }
    } catch (error) {
      console.error("Error persisting reorder:", error)
    }
  }
}
